import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useSelectedRecommendedProjectSelector } from "../../../store/projects/selectors";
import { dataStyle, mapStyles } from "./GoogleMapStyle";
import LoadingSpinner from "../../../components/LoadingSpinner";
import ReactDOMServer from "react-dom/server";
import type { Feature, FeatureCollection, GeoJsonObject, GeometryObject } from "geojson";

export interface FeatureCollectionWithMetadata<
  G extends GeometryObject = GeometryObject,
  P = any,
  M = any,
> extends FeatureCollection<G, P> {
  metadata?: M;
}

export type CiidMapProps = {
  geoJsonObjects?: Array<FeatureCollectionWithMetadata<any, any, any>>;
  readonly mapContainerStyle: CSSProperties;
};

export function RecommendationMap({ geoJsonObjects, mapContainerStyle }: CiidMapProps) {
  const selectedProjectData = useSelectedRecommendedProjectSelector();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({
    lat: 20.5937,
    lng: 78.9629,
  });
  const [mapZoom, setMapZoom] = useState<number>(4);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadingOverlayRef = useRef<HTMLDivElement | null>(null);

  // Calculate map center and zoom dynamically
  const calculateMapCenterAndZoom = (bounds: google.maps.LatLngBounds) => {
    if (!bounds || bounds.isEmpty()) return;

    // Get the center of the bounds
    const center = bounds.getCenter();
    setMapCenter({
      lat: center.lat(),
      lng: center.lng(),
    });

    // Custom zoom calculation based on bounds
    // You might need to adjust these calculations based on your specific use case
    const northeast = bounds.getNorthEast();
    const southwest = bounds.getSouthWest();

    const latDiff = Math.abs(northeast.lat() - southwest.lat());
    const lngDiff = Math.abs(northeast.lng() - southwest.lng());

    // Calculate a zoom level based on the area of the bounds
    // This is a simplified approach and might need fine-tuning
    const calculateZoom = () => {
      const area = latDiff * lngDiff;
      if (area < 0.1) return 10; // Small area, zoom in close
      if (area < 1) return 8; // Medium area
      if (area < 5) return 6; // Larger area
      return 4; // Very large area
    };

    setMapZoom(calculateZoom());
  };
  // useEffect(() => {
  //   return () => {
  //     map?.data.forEach((feature) => map.data.remove(feature));

  //     setMap(null);
  //     window.gc?.();
  //   };
  // }, []);
  // Load GeoJSON into map
  useEffect(() => {
    if (!map || !geoJsonObjects?.length) return;

    // Check if Google Maps API is loaded
    if (typeof google === "undefined" || !google.maps) {
      console.error("Google Maps API not loaded");
      return;
    }

    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    // Clear existing features
    try {
      map.data.forEach((feature) => map.data.remove(feature));
    } catch (error) {
      console.error("Error clearing map features:", error);
    }

    // Add GeoJSON data
    try {
      timeoutId = setTimeout(() => {
        if (!isMounted || !map) return;

        try {
          geoJsonObjects.forEach((geoJsonObject) => {
            map.data.addGeoJson(geoJsonObject);
          });

          map.data.setStyle(dataStyle);

          // Recalculate bounds after adding GeoJSON
          const bounds = new google.maps.LatLngBounds();
          map.data.forEach((feature) => {
            const geometry = feature.getGeometry();
            if (geometry) {
              processGeometry(geometry, bounds);
            }
          });

          // Calculate and set map center and zoom
          calculateMapCenterAndZoom(bounds);

          // Fit map to GeoJSON bounds
          if (!bounds.isEmpty()) {
            map.fitBounds(bounds);
          }

          if (isMounted) {
            setIsLoading(false);
          }
        } catch (error) {
          console.error("Error processing GeoJSON:", error);
          if (isMounted) {
            setIsLoading(false);
          }
        }
      }, 500);
    } catch (error) {
      console.error("Error adding GeoJSON to map:", error);
      if (isMounted) {
        setIsLoading(false);
      }
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [map, geoJsonObjects?.length, selectedProjectData]);

  // Process GeoJSON geometry to extend bounds
  const processGeometry = (
    geometry: google.maps.Data.Geometry,
    bounds: google.maps.LatLngBounds,
  ) => {
    if (geometry.getType() === "Polygon") {
      const polygon = geometry as google.maps.Data.Polygon;
      polygon.getArray().forEach((path) => {
        path.getArray().forEach((latLng) => bounds.extend(latLng));
      });
    } else if (geometry.getType() === "MultiPolygon") {
      const multiPolygon = geometry as google.maps.Data.MultiPolygon;
      multiPolygon.getArray().forEach((polygon) => {
        polygon.getArray().forEach((path) => {
          path.getArray().forEach((latLng) => bounds.extend(latLng));
        });
      });
    }
  };

  // const onLoad = (mapInstance: google.maps.Map) => {
  //   setMap(mapInstance);
  // };

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      if (!map) return;

      setMap(map);

      // Ensure loading overlay is created and added to the map container
      try {
        if (!loadingOverlayRef.current) {
          const loadingOverlay = document.createElement("div");
          loadingOverlay.style.position = "absolute";
          loadingOverlay.style.top = "0";
          loadingOverlay.style.left = "0";
          loadingOverlay.style.width = "100%";
          loadingOverlay.style.height = "100%";
          loadingOverlay.style.display = isLoading ? "flex" : "none";
          loadingOverlay.style.justifyContent = "center";
          loadingOverlay.style.alignItems = "center";
          loadingOverlay.style.backgroundColor = "rgba(255, 255, 255, 0.6)";
          loadingOverlay.style.zIndex = "1000";

          // Create spinner and append to overlay
          const spinnerIcon = document.createElement("div");
          spinnerIcon.innerHTML = ReactDOMServer.renderToString(<LoadingSpinner />);
          loadingOverlay.appendChild(spinnerIcon);

          loadingOverlayRef.current = loadingOverlay;
          const mapDiv = map.getDiv();
          if (mapDiv) {
            mapDiv.appendChild(loadingOverlay);
          }
        }
      } catch (error) {
        console.error("Error creating loading overlay:", error);
      }
    },
    [isLoading],
  );

  useEffect(() => {
    if (loadingOverlayRef.current) {
      loadingOverlayRef.current.style.display = isLoading ? "flex" : "none";
    }
  }, [isLoading]);

  return (
    <>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        options={{
          zoom: mapZoom,
          center: mapCenter,
          // restriction: {
          //   latLngBounds: { north: 39.5, south: 5.4, east: 90.5, west: 60.1 },
          //   strictBounds: false,
          // },
          styles: mapStyles,
          streetViewControl: false,
          mapTypeControl: false,
        }}
        onLoad={onLoad}
      />
    </>
  );
}
